import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { Plus, Trash2, Search, Pencil, Star, Users } from "lucide-react";

interface Tier {
  id: number;
  name: string;
  level: number;
  status: string;
  description: string;
  payoutMargin: number;
  isDefaultTier: boolean;
  affiliateCount: number;
  createdAt: string;
}

export function TiersPage() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editingTier, setEditingTier] = useState<Tier | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["tiers", { search }],
    queryFn: async () => {
      const qp: Record<string, string> = { pageSize: "100" };
      if (search) qp.search = search;
      const res = await api.get("/v1/tiers", qp);
      if (!res.ok) throw new Error("Failed to fetch tiers");
      return res.json();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await api.delete(`/v1/tiers/${id}`);
      if (!res.ok) throw new Error("Failed to delete");
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["tiers"] }),
  });

  const tiers: Tier[] = data?.data ?? [];

  return (
    <div>
      <div className="flex items-start justify-between mb-6">
        <PageHeader title="Affiliate Tiers" description="Manage payout tiers for affiliates" />
        <button onClick={() => { setEditingTier(null); setShowForm(true); }}
          className="inline-flex items-center gap-2 bg-gray-900 text-white px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-800">
          <Plus className="w-4 h-4" /> New Tier
        </button>
      </div>

      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input type="text" placeholder="Search tiers..." value={search} onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900" />
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-3">{[1, 2, 3].map((i) => <div key={i} className="h-16 bg-gray-100 rounded-xl animate-pulse" />)}</div>
      ) : tiers.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
          <Star className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 text-sm">No tiers created yet</p>
          <p className="text-xs text-gray-400 mt-1">Create tiers to set up payout margins for affiliate groups</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Name</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Level</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Payout Margin</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Affiliates</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Status</th>
                <th className="text-right text-xs font-medium text-gray-500 uppercase px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {tiers.map((tier) => (
                <tr key={tier.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Link to={`/tiers/${tier.id}` as any} className="text-sm font-medium text-blue-600 hover:underline">{tier.name}</Link>
                      {tier.isDefaultTier && (
                        <span className="inline-flex items-center gap-0.5 text-xs font-medium text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded-full">
                          <Star className="w-3 h-3" /> Default
                        </span>
                      )}
                    </div>
                    {tier.description && <p className="text-xs text-gray-400 mt-0.5 truncate max-w-xs">{tier.description}</p>}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{tier.level}</td>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{tier.payoutMargin}%</td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-1 text-sm text-gray-600">
                      <Users className="w-3.5 h-3.5" /> {tier.affiliateCount}
                    </span>
                  </td>
                  <td className="px-4 py-3"><StatusBadge status={tier.status} /></td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => { setEditingTier(tier); setShowForm(true); }}
                        className="p-1.5 text-gray-400 hover:text-gray-600 rounded"><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => { if (window.confirm(`Delete "${tier.name}"?`)) deleteMutation.mutate(tier.id); }}
                        className="p-1.5 text-red-400 hover:text-red-600 rounded"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <TierFormModal
          tier={editingTier}
          onClose={() => { setShowForm(false); setEditingTier(null); }}
        />
      )}
    </div>
  );
}

function TierFormModal({ tier, onClose }: { tier: Tier | null; onClose: () => void }) {
  const queryClient = useQueryClient();
  const isEdit = !!tier;

  const [name, setName] = useState(tier?.name ?? "");
  const [level, setLevel] = useState(String(tier?.level ?? 1));
  const [description, setDescription] = useState(tier?.description ?? "");
  const [payoutMargin, setPayoutMargin] = useState(String(tier?.payoutMargin ?? 0));
  const [isDefaultTier, setIsDefaultTier] = useState(tier?.isDefaultTier ?? false);
  const [status, setStatus] = useState(tier?.status ?? "active");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const payload = {
        name,
        level: Number(level),
        description,
        payoutMargin: Number(payoutMargin),
        isDefaultTier,
        status,
      };
      const res = isEdit
        ? await api.patch(`/v1/tiers/${tier!.id}`, payload)
        : await api.post("/v1/tiers", payload);
      if (!res.ok) { const d = await res.json(); throw new Error(d.error || "Failed"); }
      queryClient.invalidateQueries({ queryKey: ["tiers"] });
      onClose();
    } catch (err: any) { setError(err.message); } finally { setLoading(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-lg w-full max-w-md p-6">
        <h2 className="text-lg font-semibold mb-4">{isEdit ? "Edit" : "Create"} Tier</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && <div className="bg-red-50 text-red-700 text-sm p-3 rounded-lg border border-red-200">{error}</div>}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
            <input type="text" required value={name} onChange={(e) => setName(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
              placeholder="e.g., Gold Partners" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Level</label>
              <input type="number" min="1" value={level} onChange={(e) => setLevel(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Payout Margin (%)</label>
              <input type="number" step="0.1" value={payoutMargin} onChange={(e) => setPayoutMargin(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" placeholder="Optional description..." />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select value={status} onChange={(e) => setStatus(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">
                <option value="active">Active</option>
                <option value="paused">Paused</option>
              </select>
            </div>
            <div className="flex items-end pb-1">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={isDefaultTier} onChange={(e) => setIsDefaultTier(e.target.checked)}
                  className="rounded border-gray-300 text-gray-900 focus:ring-gray-900" />
                <span className="text-sm text-gray-700">Default tier</span>
              </label>
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 disabled:opacity-50">
              {loading ? "Saving..." : isEdit ? "Save Changes" : "Create Tier"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
